import { useState, useEffect, useRef } from "react";
import { Upload, Trash2, Copy } from "lucide-react";
import api, { imgUrl, formatApiError } from "../../lib/api";
import { toast } from "sonner";

export default function AdminMedia() {
  const [files, setFiles] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/admin/media").then((r) => setFiles(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const list = [...e.target.files];
    if (!list.length) return;
    setUploading(true);
    try {
      for (const file of list) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post("/admin/upload", fd);
      }
      toast.success("Uploaded");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (f) => {
    if (!window.confirm(`Delete "${f.original_filename}"?`)) return;
    try {
      await api.delete(`/admin/media/${f.id}`);
      toast.success("Image deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!files) return <div className="h-40 bg-muted animate-pulse" />;

  return (
    <div data-testid="admin-media">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-serif text-3xl font-semibold">Media Library <span className="text-sm text-muted-foreground font-sans font-normal">({files.length})</span></h1>
        <button onClick={() => fileRef.current?.click()} disabled={uploading}
          className="bg-[#111] text-[#FAF9F6] px-5 py-2.5 text-xs uppercase tracking-widest hover:bg-gold hover:text-[#111] transition-colors duration-300 flex items-center gap-2"
          data-testid="media-upload-button">
          <Upload strokeWidth={1.5} size={14} /> {uploading ? "Uploading..." : "Upload Images"}
        </button>
      </div>
      <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={upload} data-testid="media-file-input" />
      <p className="text-xs text-muted-foreground mb-6">Images are auto-compressed without visible quality loss. Used images cannot be deleted. Assign images to sections from Website Control or product/category pages.</p>

      {files.length === 0 ? (
        <p className="text-sm text-muted-foreground bg-white border border-black/10 p-12 text-center" data-testid="no-media">No images uploaded yet.</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {files.map((f) => (
            <div key={f.id} className="bg-white border border-black/10 group" data-testid={`media-${f.id}`}>
              <div className="aspect-square overflow-hidden bg-muted">
                <img src={imgUrl(`/api/files/${f.storage_path}`)} alt={f.original_filename} loading="lazy" className="w-full h-full object-cover" />
              </div>
              <div className="p-2 flex items-center justify-between gap-1">
                <p className="text-[10px] text-muted-foreground truncate flex-1">{f.original_filename}{f.width ? ` · ${f.width}×${f.height}` : ""}</p>
                <button onClick={() => { navigator.clipboard.writeText(imgUrl(`/api/files/${f.storage_path}`)); toast.success("URL copied"); }} className="p-1 hover:text-gold transition-colors duration-300" data-testid={`media-copy-${f.id}`} aria-label="Copy URL">
                  <Copy strokeWidth={1.5} size={13} />
                </button>
                <button onClick={() => remove(f)} className="p-1 hover:text-destructive transition-colors duration-300" data-testid={`media-delete-${f.id}`} aria-label="Delete">
                  <Trash2 strokeWidth={1.5} size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
